import axios from "axios";
import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../../../Utlils";
import { useGlobalContext } from "../../../../Contexts/GlobalContext";
import { CircularProgress } from "@mui/material";
import { FaGlobe, FaRobot, FaNetworkWired, FaIdCard, FaFingerprint, FaCheckCircle, FaCookie } from "react-icons/fa";
import { MdPhoneIphone, MdTablet, MdComputer } from "react-icons/md";

const Security = ({ data, getSingleSurvey }) => {
  const [inputData, setInputData] = useState({});
  const [deviceType, setDeviceType] = useState({});
  const [loading, setLoading] = useState(false);
  const { setSnackbar } = useGlobalContext();

  const handleChange = (e, isDeviceType) => {
    const { name, checked } = e.target;
    if (isDeviceType) {
      setDeviceType((prev) => ({ ...prev, [name]: checked }));
    } else {
      setInputData((prev) => ({ ...prev, [name]: checked }));
    }
  };

  const deviceTypeData = [
    { key: "mobile", label: "Mobile", icon: MdPhoneIphone },
    { key: "tablet", label: "Tablet", icon: MdTablet },
    { key: "desktop", label: "Desktop", icon: MdComputer },
  ];

  const securityOptions = [
    {
      name: "unique_ip",
      label: "Unique IP Address",
      description: "Prevent multiple submissions from the same IP address",
      icon: FaNetworkWired,
    },
    {
      name: "unique_rid",
      label: "Unique RID",
      description: "Ensure each respondent ID is used only once",
      icon: FaIdCard,
    },
    {
      name: "unique_fingerprint",
      label: "Unique Fingerprint",
      description: "Detect and prevent duplicate submissions using browser fingerprinting",
      icon: FaFingerprint,
    },
    {
      name: "verify_callbacks",
      label: "Verify Callbacks",
      description: "Verify callback URLs to ensure data integrity",
      icon: FaCheckCircle,
    },
    {
      name: "cookie_duplication",
      label: "Cookie Duplication",
      description: "Block a respondent who already has this survey’s cookie",
      icon: FaCookie,
    },
  ];

  const alwaysEnabledOptions = [
    {
      label: "Geo Location",
      description: "Automatically tracks and validates geographic location",
      icon: FaGlobe,
    },
    {
      label: "Bot Detection",
      description: "Advanced bot detection enabled by default",
      icon: FaRobot,
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${BASE_URL}/survey?id=${data?._id}`, {
        security_checks: inputData,
        deviceType,
      });
      getSingleSurvey();
      setSnackbar({
        open: true,
        msg: "Survey Security Updated!",
        severity: "success",
      });
    } catch (er) {
      setSnackbar({
        msg: "Something went wrong!",
        open: true,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setInputData({
      ...(data?.security_checks ?? {}),
      cookie_duplication: data?.security_checks?.cookie_duplication !== false,
    });
    setDeviceType(data?.deviceType ?? {});
  }, [data]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col w-full gap-[32px]">
      {/* Security Validation Section */}
      <div className="w-full">
        <div className="w-full tableBorder py-4 px-6 bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-600">
          <h1 className="text-red-700 text-[22px] font-[700]">
            Security Validation
          </h1>
          <p className="text-[#666] text-[14px] font-[400] mt-1">
            Configure security checks to protect your survey from fraud and duplicate submissions.
          </p>
        </div>
        <div className="w-full tableBorder pt-6 pb-8">
          <div className="w-full px-10 flex flex-col gap-[32px]">
            {/* Always Enabled Features */}
            <div>
              <h2 className="text-[#333] text-[16px] font-[600] mb-4">
                Always Active
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {alwaysEnabledOptions.map((option, index) => {
                  const IconComponent = option.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg"
                    >
                      <IconComponent className="text-2xl text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-[#333] font-[600] text-[14px]">
                            {option.label}
                          </h3>
                          <span className="px-2 py-0.5 bg-green-600 text-white text-[10px] font-[600] rounded">
                            ACTIVE
                          </span>
                        </div>
                        <p className="text-[#666] text-[12px]">{option.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Configurable Security Options */}
            <div>
              <h2 className="text-[#333] text-[16px] font-[600] mb-4">
                Security Options
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {securityOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <div
                      key={option.name}
                      className="flex items-start gap-4 p-4 border border-[#E0E0E0] rounded-lg hover:border-[#0226BE] transition-all bg-white"
                    >
                      <IconComponent className="text-2xl text-[#0226BE] flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-[#333] font-[600] text-[14px]">
                            {option.label}
                          </h3>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              name={option.name}
                              checked={inputData?.[option.name] || false}
                              onChange={handleChange}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#0226BE] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0226BE]"></div>
                          </label>
                        </div>
                        <p className="text-[#666] text-[12px]">{option.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Device Type Section */}
      <div className="w-full">
        <div className="w-full tableBorder py-4 px-6 bg-gradient-to-r from-indigo-50 to-blue-50 border-l-4 border-indigo-600">
          <h1 className="text-indigo-700 text-[22px] font-[700]">
            Device Type Access
          </h1>
          <p className="text-[#666] text-[14px] font-[400] mt-1">
            Select which device types can access this survey.
          </p>
        </div>
        <div className="w-full tableBorder pt-6 pb-8">
          <div className="w-full px-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {deviceTypeData.map((device) => {
                const IconComponent = device.icon;
                return (
                  <div
                    key={device.key}
                    className={`flex items-center justify-between p-4 border-2 rounded-lg transition-all ${
                      deviceType?.[device.key]
                        ? "border-[#0226BE] bg-blue-50"
                        : "border-[#E0E0E0] bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <IconComponent className={`text-2xl flex-shrink-0 ${
                        deviceType?.[device.key] ? "text-[#0226BE]" : "text-gray-400"
                      }`} />
                      <div>
                        <h3 className="text-[#333] font-[600] text-[14px] capitalize">
                          {device.label}
                        </h3>
                        <p className="text-[#666] text-[12px]">
                          {device.key === "mobile"
                            ? "Smartphones & mobile devices"
                            : device.key === "tablet"
                            ? "Tablets & iPads"
                            : "Desktop & laptop computers"}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name={device.key}
                        checked={deviceType?.[device.key] || false}
                        onChange={(e) => handleChange(e, true)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#0226BE] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0226BE]"></div>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="w-full flex justify-end gap-4 pt-4 pb-6">
        <button
          type="submit"
          disabled={loading}
          className={`px-8 py-3 rounded-md font-[600] text-[14px] transition-all flex items-center gap-2 ${
            loading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-[#0226BE] text-white hover:bg-[#0219a0] shadow-md hover:shadow-lg"
          }`}
        >
          {loading ? (
            <>
              <CircularProgress size={16} className="text-white" />
              Saving...
            </>
          ) : (
            "Save Security Settings"
          )}
        </button>
      </div>
    </form>
  );
};

export default Security;
